import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import {
    dropdownTriggerIconWidthEm,
    iconButtonInteractiveStyles,
} from 'components/dropdown/styled';
import { defaultInputPadding, TextInput } from 'components/forms/TextInput';
import { InfiniteScroll } from 'components/infinite_scroll/InfiniteScroll';
import { InfoPopup } from 'components/popup/InfoPopup';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { InfiniteDataSource } from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import { useHighlightedIndex } from 'custom_hooks/useHighlightedIndex';
import { usePopup } from 'custom_hooks/usePopup';
import {
    forwardRef,
    MouseEventHandler,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useResizeAware from 'react-resize-aware';
import { getInputValue } from 'shared/autocomplete/shared';
import { Popup } from 'shared/popup/Popup';
import styled, { css } from 'styled-components';
import { blockLeadingSpaces } from 'utils/input_handlers';

const FinalItem = styled.div`
    border-top: 1px solid var(--border-default);
`;

const Wrapper = styled.div`
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    position: relative;
`;

const InputBox = styled(TextInput)`
    width: 100%;
    border: 2px solid var(--border-default);
    border-width: 2px !important;
    border-radius: 0.3rem;
    background-color: var(--input-background);

    padding-left: ${defaultInputPadding};
    padding-bottom: ${defaultInputPadding};
    /* account for the chevron and clear buttons, so text does not overlap */
    padding-right: 5.5rem;

    &:disabled {
        cursor: not-allowed;
        color: var(--text-disabled);
    }
`;

const sharedIconStyles = css`
    display: inline-block;
    position: absolute;
    color: var(--text-primary);
    font-size: 1.4rem;
`;

const ClearButton = styled(Button)`
    ${sharedIconStyles}
    right: 3.5rem;
    ${iconButtonInteractiveStyles}
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Chevron = styled.div<{ $disabled?: boolean }>`
    ${sharedIconStyles}
    width: ${dropdownTriggerIconWidthEm}em;
    right: 1.2rem;
    color: var(--text-primary);
    cursor: pointer;
    padding-bottom: 0.2rem;

    ${({ $disabled }) =>
        $disabled
            ? css`
                  color: var(--text-disabled);
                  cursor: not-allowed;
              `
            : ''}
`;

const Options = styled.ul`
    display: flex;
    flex-direction: column;

    /* css reset */
    margin: 0;
    padding: 0;
`;

const OptionItem = styled.li`
    text-align: left;
    padding: 1rem 2rem;
    border-radius: 0;
    margin: 0;
    text-indent: 0;
    list-style-type: none;

    &:hover {
        background-color: var(--action-hover);
    }

    &[aria-selected='true'] {
        background-color: var(--action-selected);
    }
`;

type OptionProps = React.PropsWithChildren<
    {
        isSelected?: boolean;
        isHighlighted?: boolean;
    } & React.HTMLAttributes<HTMLLIElement>
>;

const Option = forwardRef<HTMLLIElement, OptionProps>(function Option(
    { children, isSelected, isHighlighted, ...rest },
    ref
) {
    return (
        <OptionItem
            {...rest}
            aria-selected={isSelected || isHighlighted || undefined}
            ref={ref}
        >
            {children}
        </OptionItem>
    );
});

const equals = (a: string, b: string, caseInsensitive?: boolean) => {
    return caseInsensitive ? a.toLowerCase() === b.toLowerCase() : a === b;
};

/**
 * The value of the input. `TData` is some object value selected from the list of `items`,
 * while `string` is used to distinguish user-entered values that are not in `items`.
 * TData must not be of type string!
 */
export type AutoCompleteValue<TData> = TData | string | null;

export type AutoCompleteProps<TData, TFilter> = Pick<
    InfiniteDataSource<TData, TFilter>,
    'results' | 'hasNext' | 'isLoading' | 'loadNext'
> &
    Readonly<{
        /**
         * The value of the input. `TData` is some object value selected from the list of `items`,
         * while `string` is used to distinguish user-entered values that are not in `items`.
         */
        value: AutoCompleteValue<TData> | undefined;
        /**
         * The value in the text box used for adding a custom item.
         */
        inputValue: string;
        /**
         * Callback for when `inputValue` changes.
         */
        onInputValueChange: (inputValue: string) => void;
        /**
         * Disables the input.
         */
        disabled?: boolean;
        /**
         * Toggles case insensitivity for the input. Default is case sensitive.
         */
        caseInsensitive?: boolean;
        reset: () => void;
        /**
         * The placeholder value.
         */
        placeholder?: string;
        /**
         * A function for getting a unique key for the item.
         */
        dataKey: (item: TData) => string;
        /**
         * A function for rendering the item as a string, used to set input value and for searching.
         */
        renderString: (item: TData) => string;
        /**
         * A function to render the item.
         */
        renderItem: (item: TData) => ReactNode;
        /**
         * Callback for when the `value` changes. `value` here may be a `string` user-entered value, or may be
         * a value selected from `items`.
         */
        onChange: (value: AutoCompleteValue<TData>) => void;
        /**
         * Text to describe the resource that can be created.
         * Example: `an entry`, `a Manufacturer`
         */
        entityText: string;
        /**
         * text for messaging
         *
         * @example
         * in "Type the model number of the Sensor to add it!",
         * "the model number of the Sensor"
         */
        thingToType?: string;
    }>;

export function AutoComplete<TData, TFilter>({
    value,
    inputValue,
    onInputValueChange,
    disabled,
    caseInsensitive,
    reset,
    results,
    isLoading,
    hasNext,
    loadNext,
    placeholder,
    dataKey,
    renderString,
    renderItem,
    onChange,
    entityText,
    thingToType,
}: AutoCompleteProps<TData, TFilter>) {
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const isAddOptionHighlighted = highlightedIndex >= (results?.length ?? 0);
    const [resizeListener, { width: parentWidth }] = useResizeAware();
    const { isOpen, open, close, triggerRef, popupRef } = usePopup<HTMLInputElement>();
    const addOptionRef = useRef<HTMLLIElement>(null);

    const inputExists = useMemo(
        () =>
            equals(inputValue, getInputValue(value, renderString), caseInsensitive) ||
            (results &&
                results.some((item) =>
                    equals(renderString(item), inputValue, caseInsensitive)
                )),
        [inputValue, value, renderString, caseInsensitive, results]
    );

    const [onSelectListOption, onSelectAddOption] = (() => {
        const onSelectOption = (item: AutoCompleteValue<TData>) => {
            onChange(item);
            close();
        };
        return [
            (item: TData | null) => onSelectOption(item),
            () => onSelectOption(inputValue),
        ];
    })();

    const onFilterKeyDown = useHighlightedIndex({
        items: results ?? [],
        highlightedIndex,
        onChange: (idx, boundsMeta) => {
            if (boundsMeta === 'afterLast') {
                setHighlightedIndex(results?.length ?? 0);
                addOptionRef.current?.scrollIntoView({
                    behavior: 'instant',
                    block: 'nearest',
                    inline: 'nearest',
                });
                return;
            }
            setHighlightedIndex(idx);

            if (results) {
                document.getElementById(`opt-${dataKey(results[idx])}`)?.scrollIntoView({
                    behavior: 'instant',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        },
        onSelect: (item) => {
            onSelectListOption(item);
        },
        isDisabled: !isOpen,
    });

    const onClear: MouseEventHandler = (event) => {
        event.stopPropagation();
        event.preventDefault();
        onSelectListOption(null);
        onInputValueChange('');
    };

    useEffect(() => {
        setHighlightedIndex(0);
    }, [results]);

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    useEffect(() => {
        if (disabled) {
            return;
        }
        // if inputExists, we likely just selected something -- and, at least,
        // there's less reason to go searching
        if (inputValue && !inputExists) {
            open();
        }
    }, [disabled, inputExists, inputValue, open]);

    useEffect(() => {
        if (disabled) {
            close();
        }
    }, [close, disabled]);

    const hasAnySelection = !!(inputValue && inputExists);
    const hasTypedButNotSelected = !!(inputValue && !inputExists);
    const isAddSelected = typeof value === 'string';

    return (
        <>
            <div className="position-relative">{resizeListener}</div>
            <Wrapper>
                {isAddSelected && (
                    <div style={{ position: 'absolute', left: '1.2rem' }}>
                        <InfoPopup
                            iconOverride={faPlusCircle}
                            title={`"${value}" + ' will be created`}
                        >
                            {entityText[0].toUpperCase() + entityText.substring(1)} named
                            &quot;{value}&quot; will be created when you submit this form.
                        </InfoPopup>
                    </div>
                )}
                <InputBox
                    style={{
                        paddingLeft: isAddSelected ? '3rem' : undefined,
                    }}
                    disabled={disabled}
                    autofocus={false}
                    ref={triggerRef}
                    value={inputValue}
                    role="combobox"
                    aria-autocomplete="both"
                    placeholder={placeholder}
                    onClick={open}
                    onKeyDown={(e) => {
                        if (isOpen) {
                            onFilterKeyDown(e);
                        }
                        switch (e.key) {
                            case 'Enter': {
                                if (!isOpen) {
                                    open();
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                                if (isOpen && isAddOptionHighlighted) {
                                    onSelectAddOption();
                                    // necessary to prevent the "onClear" click
                                    // event from immediately firing
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                                break;
                            }
                            case 'Escape': {
                                close();
                                break;
                            }
                            default:
                        }
                    }}
                    onBlur={close}
                    onChange={blockLeadingSpaces(
                        (e) => onInputValueChange(e.target.value),
                        'Search may not begin with spaces'
                    )}
                />
                {value !== null && (
                    <ClearButton onClick={onClear}>
                        <FontAwesomeIcon icon={faXmark} />
                    </ClearButton>
                )}
                <Chevron $disabled={disabled}>
                    <FontAwesomeIcon icon={faChevronDown} />
                </Chevron>
            </Wrapper>
            {isOpen && (
                <Popup
                    anchorRef={triggerRef}
                    ref={popupRef}
                    test-id="autocomplete-popup"
                    popupStyle={{ width: parentWidth ?? undefined }}
                >
                    <SpinnerArea isLoading={isLoading}>
                        <InfiniteScroll
                            onLoadMore={loadNext}
                            isLoading={isLoading}
                            enabled={hasNext ?? false}
                            style={{ maxHeight: '30rem' }}
                        >
                            <Options
                                role="listbox"
                                // prevent onBlur from the input if click inside
                                // the related popup
                                onMouseDownCapture={(e) => {
                                    e.preventDefault();
                                }}
                            >
                                {results?.map((item, idx) => (
                                    <Option
                                        key={dataKey(item)}
                                        id={`opt-${dataKey(item)}`}
                                        role="option"
                                        onClick={() => onSelectListOption(item)}
                                        isSelected={item === value}
                                        isHighlighted={idx === highlightedIndex}
                                    >
                                        {renderItem(item)}
                                    </Option>
                                ))}

                                {hasTypedButNotSelected ? (
                                    <Option
                                        isHighlighted={isAddOptionHighlighted}
                                        onClick={() => onSelectAddOption()}
                                        ref={addOptionRef}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlusCircle}
                                            className="mr-5"
                                        />{' '}
                                        Add &quot;
                                        <span className="word-wrap-anywhere">
                                            {inputValue}
                                        </span>
                                        &quot;
                                    </Option>
                                ) : (
                                    <FinalItem className="text-center p-20">
                                        {hasAnySelection ? (
                                            <>
                                                Don&apos;t see what you&apos;re looking
                                                for? Type{' '}
                                                {thingToType ??
                                                    `the name of ${entityText}`}{' '}
                                                to add it!
                                            </>
                                        ) : (
                                            <>
                                                Don&apos;t see what you&apos;re looking
                                                for? Start typing {thingToType} to add{' '}
                                                {thingToType ? 'it' : entityText}!
                                            </>
                                        )}
                                    </FinalItem>
                                )}
                            </Options>
                        </InfiniteScroll>
                    </SpinnerArea>
                </Popup>
            )}
        </>
    );
}

export type CreateItemTextInputProps = Readonly<{
    value: string | null | undefined;
    /**
     * Disables the input.
     */
    disabled?: boolean;
    /**
     * Callback for when the `value` changes. `value` here will be a `string` user-entered value
     */
    onChange: (value: string | null) => void;
    /**
     * Text to describe the resource that can be created.
     * Example: `an entry`, `a Manufacturer`
     */
    entityText: string;
    placeholder?: string;
}>;

export function CreateItemTextInput({
    value,
    disabled,
    placeholder,
    onChange,
    entityText,
}: CreateItemTextInputProps) {
    return (
        <Wrapper>
            <div style={{ position: 'absolute', left: '1.2rem' }}>
                <InfoPopup
                    iconOverride={faPlusCircle}
                    title={`"${value}" + ' will be created`}
                >
                    {entityText[0].toUpperCase() + entityText.substring(1)} named &quot;
                    {value}&quot; will be created when you submit this form.
                </InfoPopup>
            </div>
            <InputBox
                style={{
                    paddingLeft: '3rem', // for 'add' icon
                }}
                disabled={disabled}
                autofocus={false}
                value={value ?? ''}
                role="combobox"
                aria-autocomplete="both"
                placeholder={placeholder}
                onChange={blockLeadingSpaces((e) => onChange(e.target.value))}
            />
            {value !== null && (
                <ClearButton onClick={() => onChange(null)} style={{ right: '1rem' }}>
                    <FontAwesomeIcon icon={faXmark} />
                </ClearButton>
            )}
        </Wrapper>
    );
}
